<script setup lang="ts">
/**
 * Error summary („Fehlerübersicht“): the refused writes of one context — a
 * section, a dialog, a grid — in one `danger` banner at its top. It renders
 * nothing while `entries` is empty.
 *
 * One entry: its `title` (or the kit text „Vorgang fehlgeschlagen“) above its
 * `detail`. Several: the counted title „{count} Vorgänge fehlgeschlagen“ above
 * a list. Both forms lay an entry out the same way: the area in bold, the
 * detail, and the entry's actions after the text, wrapping under it when the
 * context is narrow.
 *
 * Per entry „Fehler-ID kopieren“ when it carries a `requestId` (the kit's copy
 * button; emits `copied` once the id is in the clipboard, `copyFailed` when the
 * clipboard refuses — the id is on screen nowhere, so the caller says so) and
 * „Neu laden“ when `reload` is set (emits `reload` with the entry's id). There
 * is no close control on purpose: an entry leaves when its originator succeeds
 * or starts again, which is the owner's bookkeeping, never a click here.
 */
import { computed, inject, useId, useTemplateRef } from 'vue';
import { NxBanner } from '../Banner';
import { NxButton } from '../Button';
import { NxCopyButton } from '../Copy';
import { i18nInjectionKey } from '../internal';
import type { NxErrorSummaryEntry } from './types';

const props = defineProps<{
  /** The refused writes; empty renders nothing. */
  entries: readonly NxErrorSummaryEntry[];
}>();

const emit = defineEmits<{
  /** „Neu laden“ of the entry with this id was clicked. */
  reload: [id: string];
  /** The request id landed in the clipboard. */
  copied: [requestId: string];
  /** The clipboard refused the request id; the summary shows it nowhere, so tell the user. */
  copyFailed: [requestId: string];
}>();

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string, params?: Record<string, unknown>): string {
  const full = `common.error_summary.${key}`;
  const text = i18n?.t(full, params);
  if (text && text !== full) return text;
  return params
    ? fallback.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? ''))
    : fallback;
}

const isSingle = computed(() => props.entries.length === 1);

const title = computed(() =>
  isSingle.value
    ? props.entries[0]?.title || tr('one_failed', 'Vorgang fehlgeschlagen')
    : tr('n_failed', '{count} Vorgänge fehlgeschlagen', { count: props.entries.length })
);

const copyLabel = computed(() => tr('copy_request_id', 'Fehler-ID kopieren'));
const copiedLabel = computed(() => tr('copied', 'Kopiert'));
const reloadLabel = computed(() => tr('reload', 'Neu laden'));

function hasActions(entry: NxErrorSummaryEntry): boolean {
  return !!entry.requestId || !!entry.reload;
}

// Several rows carry the same button names; each row's actions form a group
// named by the row's text, so „Fehler-ID kopieren“ says which error it is for.
const baseId = useId();
function textId(index: number): string {
  return `${baseId}-entry-${index}`;
}

const rootRef = useTemplateRef<{ $el: Element }>('root-ref');

/** Moves focus to the summary (a dialog does so after a deliberate submit). */
function focus() {
  const el = rootRef.value?.$el;
  if (el instanceof HTMLElement) el.focus();
}

defineExpose({ focus });
</script>

<template>
  <NxBanner
    v-if="props.entries.length > 0"
    ref="root-ref"
    tabindex="-1"
    class="nx-error-summary"
    variant="danger"
    align="start"
    role="alert"
    :title="title"
  >
    <!-- One entry is a plain row, several a list; the row is the same. -->
    <component :is="isSingle ? 'div' : 'ul'" class="nx-error-summary__list">
      <component
        :is="isSingle ? 'div' : 'li'"
        v-for="(entry, index) in props.entries"
        :key="entry.id"
        class="nx-error-summary__entry"
      >
        <span :id="textId(index)" class="nx-error-summary__text">
          <b v-if="entry.area">{{ entry.area }}:</b>
          {{ entry.detail }}
        </span>
        <span
          v-if="hasActions(entry)"
          class="nx-error-summary__actions"
          role="group"
          :aria-labelledby="textId(index)"
        >
          <NxCopyButton
            v-if="entry.requestId"
            :value="entry.requestId"
            :label="copyLabel"
            :copied-label="copiedLabel"
            height="xs"
            variant="ghost"
            data-action="copy-request-id"
            @copied="emit('copied', $event)"
            @error="emit('copyFailed', entry.requestId)"
          />
          <NxButton
            v-if="entry.reload"
            height="xs"
            variant="ghost"
            data-action="reload"
            @click="emit('reload', entry.id)"
          >
            {{ reloadLabel }}
          </NxButton>
        </span>
      </component>
    </component>
  </NxBanner>
</template>

<style scoped>
/* Long words (an id, a path in the backend's sentence) break instead of
   pushing the banner wider than a narrow section. */
.nx-error-summary__text {
  overflow-wrap: anywhere;
}
.nx-error-summary__list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  /* The global type reset sizes every bare <ul> at 16px medium; the list keeps
     the banner's type, like the lone entry. */
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}
/* One row per entry: the text claims a readable measure first, the actions
   sit at its end and wrap under it once the row runs out of room. */
.nx-error-summary__entry {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: var(--nxt-space-sm);
  row-gap: var(--nxt-space-2xs);
}
.nx-error-summary__entry .nx-error-summary__text {
  flex: 1 1 var(--nxt-panel-width-sm);
  min-width: 0;
}
.nx-error-summary__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-xs);
}
</style>
